import { fetchDataAudioData, dataAudioUrl } from './dataApi';

// Recorded-phrase audio lectures — mirrors the mobile app's
// AudioLecturesService (lib/features/audio/data/audio_lectures_service.dart)
// field-for-field. A target language's data-audio/{code}/playlist.json,
// grouped into themed collections (Greetings, Questions, ...) of tracks;
// `songs` collections carry only a `path` to their own sub-manifest
// (songs/catalog.json) and are resolved separately. Only a subset of target
// languages have this content recorded — loadAudioPlaylist returns null
// rather than throwing when a language has none, same "nice to have when
// present" treatment as Pronunciation Guide's per-language gaps.

// Collection kinds the lectures page renders as a tab. `phrases`, `vocab`
// and `alphabet` arrive with their tracks inline; `songs` arrives with just
// a `path` and is resolved from songs/catalog.json below. `conversation`/
// `story` collections are pointers for other pages, not shown here.
const SCREEN_TYPES = new Set(['phrases', 'vocab', 'alphabet', 'songs', '']);

function parseTrack(json) {
  return {
    order: Number.isFinite(Number(json.order)) ? Number(json.order) : null,
    id: json.id != null ? String(json.id) : '',
    title: json.title != null ? String(json.title) : '',
    translation: json.translation != null ? String(json.translation) : '',
    file: json.file != null ? String(json.file) : '',
    lyrics: null,
  };
}

// Parses a song catalog's `lyrics` map ({ "1": ["…"], "2": ["…"] }) into
// ordered stanzas, or null when absent/malformed.
function lyricsFromJson(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const stanzas = Object.entries(raw)
    .map(([key, value]) => [Number.parseInt(key, 10) || 1e9, Array.isArray(value) ? value.map(String) : []])
    .filter(([, lines]) => lines.length > 0)
    .sort((a, b) => a[0] - b[0])
    .map(([, lines]) => lines);
  return stanzas.length > 0 ? stanzas : null;
}

// Sorts by the authored `order` field, falling back to array position for
// any track missing one, so a collection that omits `order` still plays in
// file order instead of being reshuffled.
function parseCollection(json) {
  const rawTracks = Array.isArray(json.tracks)
    ? json.tracks.filter((t) => t && typeof t === 'object').map(parseTrack).filter((t) => t.id && t.file)
    : [];
  const indexed = rawTracks.map((track, index) => ({ track, order: track.order ?? index, index }));
  indexed.sort((a, b) => (a.order - b.order) || (a.index - b.index));
  return {
    id: json.id != null ? String(json.id) : '',
    type: json.type != null ? String(json.type) : '',
    title: json.title != null ? String(json.title) : '',
    icon: json.icon != null ? String(json.icon) : '🎧',
    path: json.path != null ? String(json.path) : null,
    tracks: indexed.map((e) => e.track),
  };
}

function parsePlaylist(json) {
  const collections = Array.isArray(json.collections)
    ? json.collections
      .filter((c) => c && typeof c === 'object')
      .map(parseCollection)
      .filter((c) => SCREEN_TYPES.has(c.type) && (c.tracks.length > 0 || c.path != null))
    : [];
  return {
    language: json.language != null ? String(json.language) : '',
    title: json.title != null ? String(json.title) : '',
    description: json.description != null ? String(json.description) : '',
    collections,
  };
}

// All tracks across every collection, in one continuous playback order
// (collection order, then each collection's own track order) — used to
// drive next/previous/auto-advance across collection boundaries.
export function flattenTracks(playlist) {
  return (playlist?.collections || []).flatMap((c) => c.tracks);
}

// Loads a `songs` collection's tracks from its catalog.json — a
// number-keyed { "1": { title, description, audio, lyrics }, … } map.
// `description` becomes the track subtitle and `lyrics` (ordered stanzas)
// rides along for the page to show.
async function loadSongCollection(langCode, path) {
  try {
    const json = await fetchDataAudioData(`${langCode}/${path}`);
    const dir = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
    const rows = Object.entries(json)
      .filter(([, value]) => value && typeof value === 'object')
      .filter(([, value]) => String(value.audio || '').trim())
      .sort(([a], [b]) => (Number.parseInt(a, 10) || 1e9) - (Number.parseInt(b, 10) || 1e9));
    return rows.map(([key, value]) => ({
      order: Number.parseInt(key, 10) || null,
      id: String(value.audio),
      title: value.title != null ? String(value.title) : String(value.audio),
      translation: value.description != null ? String(value.description) : '',
      file: dir ? `${dir}/${value.audio}` : String(value.audio),
      lyrics: lyricsFromJson(value.lyrics),
    }));
  } catch {
    return [];
  }
}

// v2: the data-audio tree was restructured (folders + file names) and
// playlist.json gained type/path + songs. Bumping the cache key forces a
// one-time re-fetch so clients holding a pre-restructure playlist (with
// now-deleted paths) don't 404 on every track — mirrors the mobile app's
// same cacheKey bump.
export async function loadAudioPlaylist(langCode) {
  try {
    const json = await fetchDataAudioData(`${langCode}/playlist.json`);
    const playlist = parsePlaylist(json);
    const resolved = [];
    for (const collection of playlist.collections) {
      if (collection.tracks.length === 0 && collection.path) {
        // eslint-disable-next-line no-await-in-loop
        const tracks = await loadSongCollection(langCode, collection.path);
        if (tracks.length > 0) resolved.push({ ...collection, tracks });
      } else if (collection.tracks.length > 0) {
        resolved.push(collection);
      }
    }
    if (resolved.length === 0) return null;
    return { ...playlist, collections: resolved };
  } catch {
    return null;
  }
}

// Full URL for one track's mp3, ready to hand to an <audio> element.
export function audioTrackUrl(langCode, track) {
  return dataAudioUrl(`${langCode}/${track.file}`);
}
